import { pitchOf } from "./fayin-pitch.js";

/**
 * A learner's recording cut down to the word, and brought up to a level.
 *
 * Hugh held the button, waited a moment, spoke, waited again and let go
 * (2026-10-26). The recording kept both waits and the click of the Space bar
 * at each end. All three went to the route, into the picture and into "Hear
 * yours".
 *
 * **The word is found from its voice, then widened to its edges.** The
 * voiced frames are the vowels, which a key click never is. The cut then
 * reaches back and forward over frames still loud enough to be speech,
 * bridging a short closure, so the unvoiced initial of 是 or 吃 and the stop
 * of 他 stay in. A click is left out unless the learner spoke straight into
 * it, because silence comes between the two.
 *
 * **Then it is levelled.** The loudest sample is brought to {@link peak},
 * and a quiet recording is raised by no more than {@link mostGain} so that the
 * room is not raised with it.
 *
 * A recording with nothing voiced in it is left as it is, for the route to
 * say it heard nothing.
 */

/** A frame quieter than this share of the loudest voiced frame is silence. */
const speechShare = 0.06;

/** The longest quiet stretch inside a word, in frames, such as a stop closure. */
const longestClosure = 4;

/** How far the cut may reach from the voice, in frames, for an initial. */
const furthestReach = 30;

/** Kept either side of the cut, in seconds, so nothing starts on a click. */
const margin = 0.05;

/** The level the loudest sample is brought to. */
export const peak = 0.9;

/** The most a recording is raised by. */
export const mostGain = 8;

/**
 * The word in a recording, levelled.
 * @param samples - Mono samples, -1 to 1.
 * @param sampleRate - Samples a second.
 * @returns The samples of the word, or the recording unchanged where no voice was found.
 */
export function trimmedVoice(
  samples: Float32Array,
  sampleRate: number,
): Float32Array {
  const frames = pitchOf(samples, sampleRate);
  const voiced = frames.flatMap((frame, at) =>
    frame.hz === undefined ? [] : [at],
  );
  const [firstVoiced] = voiced;
  const lastVoiced = voiced.at(-1);

  if (firstVoiced === undefined || lastVoiced === undefined) {
    return samples;
  }

  // Every index here is one of the frames': `voiced` was drawn from them,
  // and `reach` walks only inside them.
  const loudest = Math.max(...voiced.map((at) => frames[at]!.rms));
  const speech = (at: number): boolean =>
    frames[at]!.rms >= loudest * speechShare;

  /** Walk from the voice while the frames are speech or a short closure. */
  const reach = (from: number, step: 1 | -1): number => {
    let edge = from;
    let quiet = 0;

    for (
      let at = from + step;
      at >= 0 && at < frames.length && Math.abs(at - from) <= furthestReach;
      at += step
    ) {
      if (speech(at)) {
        edge = at;
        quiet = 0;
      } else {
        quiet += 1;

        if (quiet > longestClosure) {
          break;
        }
      }
    }

    return edge;
  };

  // `pitchOf` drops a voiced run shorter than a syllable, so a recording
  // with a voiced frame has at least two, a hop apart.
  const hop = frames[1]!.at - frames[0]!.at;
  const startAt = frames[reach(firstVoiced, -1)]!.at - hop - margin;
  const endAt = frames[reach(lastVoiced, 1)]!.at + hop + margin;
  const word = samples.slice(
    Math.max(0, Math.floor(startAt * sampleRate)),
    Math.min(samples.length, Math.ceil(endAt * sampleRate)),
  );
  const loudestSample = word.reduce(
    (most, sample) => Math.max(most, Math.abs(sample)),
    0,
  );
  // The word holds the voiced frames, so it is never silent.
  const gain = Math.min(peak / loudestSample, mostGain);

  return word.map((sample) => sample * gain);
}
